import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Wrench, X, Camera } from "lucide-react";
import PhotosManager from "./PhotosManager";

const empty = { date: "", title: "", notes: "", cost: "", author: "", before_images: [], after_images: [] };

export default function RestorationManager({ computerId }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | 'new' | id
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/computers/${computerId}/restorations`);
            setEntries(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [computerId]);

    const startNew = () => {
        setForm({ ...empty, date: new Date().toISOString().slice(0, 10) });
        setEditing("new");
    };

    const startEdit = (e) => {
        setForm({
            date: e.date || "",
            title: e.title || "",
            notes: e.notes || "",
            cost: e.cost ?? "",
            author: e.author || "",
            before_images: e.before_images || [],
            after_images: e.after_images || [],
        });
        setEditing(e.id);
    };

    const cancel = () => {
        setEditing(null);
        setForm(empty);
    };

    const submit = async (ev) => {
        ev.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                cost: form.cost === "" ? null : Number(form.cost),
                author: form.author || null,
                notes: form.notes || "",
                before_images: form.before_images || [],
                after_images: form.after_images || [],
            };
            if (editing === "new") {
                await api.post(`/computers/${computerId}/restorations`, payload);
                toast.success("Entry added.");
            } else {
                await api.put(`/computers/${computerId}/restorations/${editing}`, payload);
                toast.success("Entry updated.");
            }
            cancel();
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this restoration entry?")) return;
        try {
            await api.delete(`/computers/${computerId}/restorations/${id}`);
            toast.success("Entry removed.");
            load();
        } catch {
            toast.error("Delete failed.");
        }
    };

    return (
        <div data-testid="restoration-manager" className="border border-border">
            <div className="flex items-center justify-between border-b border-border bg-muted/30 px-6 py-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber flex items-center gap-2">
                    <Wrench className="h-3 w-3" />
                    &gt; RESTORATION_LOG.EDIT
                </div>
                {editing === null && (
                    <button
                        type="button"
                        data-testid="restoration-add-btn"
                        onClick={startNew}
                        className="inline-flex items-center gap-2 border border-phosphor-amber px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-phosphor-amber hover:bg-phosphor-amber hover:text-background transition-colors"
                    >
                        <Plus className="h-3 w-3" /> Add entry
                    </button>
                )}
            </div>

            {editing !== null && (
                <form onSubmit={submit} className="border-b border-border bg-muted/20 p-6 space-y-4" data-testid="restoration-form">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Date" testid="rest-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
                        <Field label="Author" testid="rest-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Curator" />
                        <Field label="Cost (USD)" testid="rest-cost" type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
                    </div>
                    <Field label="Title" testid="rest-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Replaced electrolytic capacitors" />
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Notes</div>
                        <textarea
                            data-testid="rest-notes"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={3}
                            className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber leading-relaxed"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <PhotosManager
                            images={form.before_images}
                            onChange={(imgs) => setForm({ ...form, before_images: imgs })}
                            label="BEFORE"
                            testIdPrefix="rest-before"
                        />
                        <PhotosManager
                            images={form.after_images}
                            onChange={(imgs) => setForm({ ...form, after_images: imgs })}
                            label="AFTER"
                            testIdPrefix="rest-after"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            type="submit"
                            data-testid="rest-submit"
                            disabled={saving}
                            className="border border-phosphor-amber bg-phosphor-amber px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-background hover:bg-transparent hover:text-phosphor-amber transition-colors disabled:opacity-50"
                        >
                            {saving ? "Saving…" : editing === "new" ? "Add entry ▸" : "Save changes ▸"}
                        </button>
                        <button
                            type="button"
                            data-testid="rest-cancel"
                            onClick={cancel}
                            className="inline-flex items-center gap-2 border border-border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-foreground"
                        >
                            <X className="h-3 w-3" /> Cancel
                        </button>
                    </div>
                </form>
            )}

            {loading ? (
                <div className="p-6 font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                    Loading log<span className="terminal-caret" />
                </div>
            ) : entries.length === 0 ? (
                <div className="p-6 font-mono text-xs text-muted-foreground" data-testid="restoration-empty">
                    &gt; No entries logged yet.
                </div>
            ) : (
                <ol className="divide-y divide-border">
                    {entries.map((e, idx) => (
                        <li key={e.id} className="grid grid-cols-12 gap-4 px-6 py-4" data-testid={`rest-row-${e.id}`}>
                            <div className="col-span-12 md:col-span-3 font-mono text-[10px] uppercase tracking-[0.25em] text-phosphor-green">
                                ● {e.date}
                                {typeof e.cost === "number" && (
                                    <div className="mt-1 text-phosphor-amber">${e.cost.toLocaleString()}</div>
                                )}
                                {((e.before_images?.length || 0) + (e.after_images?.length || 0)) > 0 && (
                                    <div className="mt-1 flex items-center gap-1 text-muted-foreground">
                                        <Camera className="h-3 w-3" />
                                        {(e.before_images?.length || 0)}b / {(e.after_images?.length || 0)}a
                                    </div>
                                )}
                            </div>
                            <div className="col-span-12 md:col-span-7">
                                <div className="font-serif text-lg text-foreground">
                                    {String(idx + 1).padStart(2, "0")}. {e.title}
                                </div>
                                {e.notes && (
                                    <p className="mt-1 font-sans text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                                        {e.notes}
                                    </p>
                                )}
                                {e.author && (
                                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        by {e.author}
                                    </p>
                                )}
                            </div>
                            <div className="col-span-12 md:col-span-2 flex md:justify-end items-start gap-2">
                                <button
                                    type="button"
                                    data-testid={`rest-edit-${e.id}`}
                                    onClick={() => startEdit(e)}
                                    className="border border-border p-2 hover:border-phosphor-amber hover:text-phosphor-amber transition-colors"
                                >
                                    <Pencil className="h-3 w-3" />
                                </button>
                                <button
                                    type="button"
                                    data-testid={`rest-delete-${e.id}`}
                                    onClick={() => remove(e.id)}
                                    className="border border-border p-2 hover:border-destructive hover:text-destructive transition-colors"
                                >
                                    <Trash2 className="h-3 w-3" />
                                </button>
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}

function Field({ label, testid, ...rest }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">{label}</div>
            <input
                data-testid={testid}
                {...rest}
                className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber"
            />
        </div>
    );
}
